import React, { useMemo } from 'react';
import { getEstadoChipClass, getMotivoChipClass } from '../../utils/styles';

/**
 * CardContrato - Componente para mostrar el resumen de un contrato ART.
 * Sincronizado con permisos de Solo Lectura y respetando estrictamente el diseño original de la interfaz.
 */
const CardContrato = ({ 
    contrato, 
    onEdit, 
    onDelete, 
    onAnular, 
    onView, 
    onNavigate, 
    onRehabilitar,
    isFullUser = true // ◄ Recibido por Props de forma síncrona (Evita múltiples listeners)
}) => {
  const hasFullAccess = isFullUser;

  // Helpers de visualización
  const formatDate = (dateString) => {
      if(!dateString) return '-';
      const parts = dateString.split('-');
      if (parts.length === 3) {
          const [y, m, d] = parts;
          return `${d}/${m}/${y}`;
      }
      return dateString;
  };

  const formatCurrency = (val) => {
      if (!val) return '$0,00';
      if (typeof val === 'number') {
           return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val);
      }
      return `$${val}`;
  };

  const displayStatus = useMemo(() => {
      if (!contrato) return '---';
      return (contrato.estadoPoliza || 'PENDIENTE').toUpperCase();
  }, [contrato]);

  const isAnulada = displayStatus === 'ANULADA';
  
  // --- LIMITACIÓN OPERATIVA DE ACCIONES POR LECTURA O ANULACIÓN ---
  const navigateRestricted = !hasFullAccess;
  const editRestricted = isAnulada || !hasFullAccess;
  const anularRestricted = isAnulada || !hasFullAccess;
  const deleteRestricted = !hasFullAccess;
  const rehabRestricted = !hasFullAccess;

    // --- Alertas ---
  const checkDebtAlert = () => {
    if (contrato?.tieneDeuda) {
        return (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 flex items-center gap-1 border border-red-200 animate-pulse shadow-sm shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                Contrato con Deuda
            </span>
        );
    }
    return null;
  };

  return (
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 overflow-hidden flex flex-col relative">
      
      {/* Barra lateral de estado (Naranja para ART, Rojo si anulada) */}
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${isAnulada ? 'bg-red-400' : 'bg-orange-500'}`}></div>

      <div className="p-6 flex-grow pl-8">
        
        {/* Fila 1: Compañía + Estado */}
        <div className="flex justify-between items-start mb-1">
            <h4 className="text-sm font-bold text-indigo-600 uppercase tracking-wider truncate pr-2 text-left">
                {contrato.compania || '---'}
            </h4>
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shrink-0 ${getEstadoChipClass(contrato.estadoPoliza)}`}>
                {contrato.estadoPoliza || 'S/D'}
            </span>
        </div>

        {/* Fila 2: Título (Contrato) */}
        <h2 className="text-xl font-extrabold text-gray-900 mb-3 flex items-center gap-2 text-left" title="Número de Contrato">
            Contrato #{contrato.nContrato || '---'}
        </h2>

        {/* Fila 3: Chip de Operación (Motivo) y Alerta de Deuda */}
        <div className="flex flex-wrap items-center gap-3 mb-5 text-left">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getMotivoChipClass(contrato.tipoOperacion)}`}>
                {contrato.tipoOperacion || 'N/A'}
            </span>

            {checkDebtAlert()}
        </div>

        {/* Fila 4: Datos Clave ART (Grid Naranja Integrado) */}
        <div className="grid grid-cols-3 gap-2 mb-5 bg-orange-50 rounded-xl border border-orange-100 p-3 text-left">
            <div className="text-center">
                <p className="text-[10px] text-gray-500 font-bold uppercase mb-1">Cápitas</p>
                <p className="text-sm font-bold text-gray-800">{contrato.capitas || 0}</p>
            </div>
            <div className="text-center border-l border-orange-200">
                <p className="text-[10px] text-gray-500 font-bold uppercase mb-1">Masa Salarial</p>
                <p className="text-sm font-bold text-gray-800">{formatCurrency(contrato.masaSalarial)}</p>
            </div>
            <div className="text-center border-l border-orange-200">
                <p className="text-[10px] text-gray-500 font-bold uppercase mb-1">Alícuota</p>
                <p className="text-sm font-bold text-gray-800">{contrato.alicuota || 0}%</p>
            </div>
        </div>

        {/* Fila 5: Datos Secundarios */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100 items-center text-left">
    
    {/* COLUMNA 1: Canal Comercial Principal */}
    <div>
        <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider mb-1">
            Canal Comercial
        </p>
        <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 bg-orange-50 text-orange-800 rounded-lg text-xs font-bold border border-orange-200 shrink-0">
                {contrato?.canal || '-'}
            </span>
            <span className="text-xs font-semibold text-gray-600 truncate" title={contrato?.canalNombre}>
                {contrato?.canalNombre || contrato?.subcanal || '-'}
            </span>
        </div>
    </div>

    {/* COLUMNA 2: Subcanal Asociado */}
    <div>
        <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider mb-1">
            Subcanal
        </p>
        <p className="text-xs font-bold text-gray-700 truncate" title={contrato?.subcanalNombre}>
            {contrato?.subcanalNombre || '-'}
        </p>
    </div>

    {/* COLUMNA 3: Período de Vigencia (Cápsula Horizontal) */}
    <div>
        <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider mb-1">
            Período de Vigencia
        </p>
        <div className="inline-flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-200/80 text-xs font-mono font-bold">
            <span className="text-gray-700">{formatDate(contrato?.inicioVigencia)}</span>
            <span className="text-gray-300">→</span>
            <span className={isAnulada ? 'text-gray-400 line-through' : 'text-rose-600'}>
                {formatDate(contrato?.finVigencia)}
            </span>
        </div>
    </div>

</div>

      </div>

      {/* Footer de Acciones */}
      <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center gap-4 text-left">
            
            {/* Botón Ir a Gestión Completa (Izquierda) */}
            <button 
                onClick={() => !navigateRestricted && onNavigate && onNavigate(contrato)} 
                disabled={navigateRestricted}
                className={`mr-auto transition-all duration-200 p-1 bg-indigo-50 rounded-full border border-indigo-100 ${
                    !navigateRestricted 
                        ? 'text-indigo-600 hover:text-indigo-800 hover:scale-110' 
                        : 'text-gray-300 bg-gray-100 border-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                }`}
                title={navigateRestricted ? "Gestión completa no disponible en modo lectura" : "Ir a Gestión Completa"}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
            </button>

            {/* Botones de Acción (Derecha) */}
            
            {/* Ver Detalle (Ojito - Siempre disponible) */}
            <button onClick={() => onView && onView(contrato)} className="text-gray-400 hover:text-blue-600 hover:scale-110 transition-all duration-200 shrink-0" title="Ver Detalle Rápido">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            </button>

            {/* Editar Contrato */}
            <button 
                onClick={() => !editRestricted && onEdit && onEdit(contrato)} 
                disabled={editRestricted} 
                className={`transition-all duration-200 shrink-0 ${
                    !editRestricted 
                        ? 'text-gray-400 hover:text-indigo-600 hover:scale-110' 
                        : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                }`} 
                title={editRestricted ? "Edición no disponible" : "Editar Contrato"}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z" /></svg>
            </button>

            {/* REHABILITAR CONTRATO (Solo visible si está anulada) */}
            {isAnulada && (
                <button 
                    onClick={() => !rehabRestricted && onRehabilitar && onRehabilitar(contrato)} 
                    disabled={rehabRestricted}
                    className={`transition-all duration-200 shrink-0 ${
                        !rehabRestricted 
                            ? 'text-emerald-500 hover:text-emerald-700 hover:scale-110' 
                            : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                    }`} 
                    title={rehabRestricted ? "Rehabilitar Contrato" : "Rehabilitación no disponible en modo lectura"}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </button>
            )}

            {/* Anular Contrato */}
            <button 
                onClick={() => !anularRestricted && onAnular && onAnular(contrato)} 
                disabled={anularRestricted} 
                className={`transition-all duration-200 shrink-0 ${
                    !anularRestricted 
                        ? 'text-gray-400 hover:text-orange-500 hover:scale-110' 
                        : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                }`} 
                title={anularRestricted ? "Anulación no disponible" : "Anular Contrato"}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
            </button>

            {/* Eliminar Definitivamente */}
            <button 
                onClick={() => !deleteRestricted && onDelete && onDelete(contrato)} 
                disabled={deleteRestricted}
                className={`transition-all duration-200 shrink-0 ${
                    !deleteRestricted 
                        ? 'text-gray-400 hover:text-red-600 hover:scale-110' 
                        : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                }`} 
                title={deleteRestricted ? "Eliminación no disponible" : "Eliminar Definitivamente"}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
      </div>
    </div>
  );
};

export default CardContrato;